import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  FormControlLabel,
  Switch,
  CircularProgress,
  Autocomplete,
  IconButton,
  Typography,
  Divider,
  Alert,
  useTheme
} from '@mui/material';
import {
  Close as CloseIcon,
  Refresh as RefreshIcon,
  Warehouse as WarehouseIcon,
  Business as BusinessIcon,
  Category as CategoryIcon,
  AccountBalance as AccountBalanceIcon,
  Person as PersonIcon,
  Language as LanguageIcon,
  Code as CodeIcon
} from '@mui/icons-material';
import { useLanguage } from '../../contexts/LanguageContext';
import { Warehouse, CreateWarehouseDto, UpdateWarehouseDto } from '../../types/warehouses.types';
import { warehousesService } from '../../services/warehouses.service';
import { chartOfAccountsService } from '../../services/chartOfAccounts.service';
import { branchesService } from '../../services/branches.service';
import axiosInstance from '../../services/axiosInstance';

interface WarehouseDialogProps {
  open: boolean;
  onClose: () => void;
  warehouse?: Warehouse;
  onSave: () => void;
}

interface Account {
  accountId: number;
  accountCode: string;
  accountName: string;
  accountNameAr: string;
  accountType: string;
}

interface BranchOption {
  branchId: number;
  branchCode: string;
  branchName: string;
  branchNameAr: string;
}

interface UserOption {
  userId: number;
  userName: string;
  fullName: string;
  fullNameAr?: string;
  isActive: boolean;
  userRoles?: { role: { roleId: number; roleName: string; roleNameAr: string } }[];
}

const warehouseTypes = [
  { value: 'Main', labelEn: 'Main Warehouse', labelAr: 'مخزن رئيسي', icon: '🏭' },
  { value: 'Branch', labelEn: 'Branch Warehouse', labelAr: 'مخزن فرع', icon: '🏪' },
  { value: 'Transit', labelEn: 'Transit Warehouse', labelAr: 'مخزن عبور', icon: '🚚' },
];

const WarehouseDialog: React.FC<WarehouseDialogProps> = ({ open, onClose, warehouse, onSave }) => {
  const { language } = useLanguage();
  const isRTL = language === 'ar';
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';

  const t = {
    addWarehouse: isRTL ? 'إضافة مخزن جديد' : 'Add New Warehouse',
    editWarehouse: isRTL ? 'تعديل مخزن' : 'Edit Warehouse',
    basicInfo: isRTL ? 'المعلومات الأساسية' : 'Basic Information',
    accountingInfo: isRTL ? 'المعلومات المحاسبية' : 'Accounting Information',
    warehouseCode: isRTL ? 'رمز المخزن' : 'Warehouse Code',
    warehouseName: isRTL ? 'اسم المخزن (إنجليزي)' : 'Warehouse Name (EN)',
    warehouseNameAr: isRTL ? 'اسم المخزن (عربي)' : 'Warehouse Name (AR)',
    branch: isRTL ? 'الفرع' : 'Branch',
    selectBranch: isRTL ? 'اختر الفرع' : 'Select Branch',
    keeper: isRTL ? 'أمين المخزن' : 'Warehouse Keeper',
    selectKeeper: isRTL ? 'اختر أمين المخزن' : 'Select Warehouse Keeper',
    warehouseType: isRTL ? 'نوع المخزن' : 'Warehouse Type',
    inventoryAccount: isRTL ? 'حساب المخزون' : 'Inventory Account',
    selectInventoryAccount: isRTL ? 'اختر حساب المخزون' : 'Select Inventory Account',
    isActive: isRTL ? 'نشط' : 'Active',
    save: isRTL ? 'حفظ' : 'Save',
    cancel: isRTL ? 'إلغاء' : 'Cancel',
    optional: isRTL ? 'اختياري' : 'Optional',
    required: isRTL ? 'مطلوب' : 'Required',
    loadingAccounts: isRTL ? 'جاري تحميل الحسابات...' : 'Loading accounts...',
    noAccountsFound: isRTL ? 'لا توجد حسابات نهائية' : 'No final accounts found',
    noBranch: isRTL ? 'بدون فرع (مخزن رئيسي)' : 'No Branch (Main Warehouse)',
    noKeeper: isRTL ? 'بدون أمين مخزن' : 'No Warehouse Keeper',
  };

  const [formData, setFormData] = useState({
    warehouseCode: '',
    warehouseName: '',
    warehouseNameAr: '',
    branchId: null as number | null,
    keeperUserId: null as number | null,
    warehouseType: 'Main',
    inventoryAccountId: null as number | null,
    isActive: true,
  });

  const [accounts, setAccounts] = useState<Account[]>([]);
  const [branchOptions, setBranchOptions] = useState<BranchOption[]>([]);
  const [userOptions, setUserOptions] = useState<UserOption[]>([]);
  const [loadingAccounts, setLoadingAccounts] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (open) {
      loadMasterData();
      if (warehouse) {
        setFormData({
          warehouseCode: warehouse.warehouseCode,
          warehouseName: warehouse.warehouseName,
          warehouseNameAr: warehouse.warehouseNameAr,
          branchId: warehouse.branchId || null,
          keeperUserId: warehouse.keeperUserId || null,
          warehouseType: warehouse.warehouseType || 'Main',
          inventoryAccountId: warehouse.inventoryAccountId || null,
          isActive: warehouse.isActive,
        });
      } else {
        loadNewWarehouseCode();
      }
    }
  }, [open, warehouse]);

  const loadMasterData = async () => {
    setLoadingAccounts(true);
    try {
      const [accountsData, branchesData, usersResponse] = await Promise.all([
        chartOfAccountsService.getAll(),
        branchesService.getAll(),
        axiosInstance.get('/Users'),
      ]);

      const finalAccounts = accountsData.filter((acc: any) =>
        acc.isFinalAccount && acc.isActive
      );
      setAccounts(finalAccounts);
      setBranchOptions(branchesData);

      const usersData = usersResponse.data?.data || usersResponse.data || [];
      // فلترة: أمين مخزن (RoleId=3) أو مدير المخزون (RoleId=9) + نشط
      const warehouseRoleIds = [3, 9];
      const warehouseUsers = usersData.filter((u: UserOption) =>
        u.isActive && u.userRoles?.some(ur => warehouseRoleIds.includes(ur.role.roleId))
      );
      setUserOptions(warehouseUsers);
    } catch (error) {
      console.error('Failed to load master data:', error);
    } finally {
      setLoadingAccounts(false);
    }
  };

  const loadNewWarehouseCode = async () => {
    try {
      const code = await warehousesService.generateCode();
      setFormData(prev => ({ ...prev, warehouseCode: code, warehouseType: 'Main' }));
    } catch (error) {
      console.error('Failed to generate warehouse code:', error);
    }
  };

  const handleGenerateCode = () => {
    loadNewWarehouseCode();
  };

  const handleChange = (field: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};
    if (!formData.warehouseName.trim()) {
      newErrors.warehouseName = isRTL ? 'اسم المخزن (إنجليزي) مطلوب' : 'Warehouse name (EN) is required';
    }
    if (!formData.warehouseNameAr.trim()) {
      newErrors.warehouseNameAr = isRTL ? 'اسم المخزن (عربي) مطلوب' : 'Warehouse name (AR) is required';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setLoading(true);
    try {
      if (warehouse) {
        const updateDto: UpdateWarehouseDto = {
          warehouseId: warehouse.warehouseId,
          warehouseName: formData.warehouseName,
          warehouseNameAr: formData.warehouseNameAr,
          branchId: formData.branchId ?? undefined,
          keeperUserId: formData.keeperUserId ?? undefined,
          warehouseType: formData.warehouseType || undefined,
          inventoryAccountId: formData.inventoryAccountId ?? undefined,
          isActive: formData.isActive,
        };
        await warehousesService.update(warehouse.warehouseId, updateDto);
      } else {
        const createDto: CreateWarehouseDto = {
          warehouseCode: formData.warehouseCode,
          warehouseName: formData.warehouseName,
          warehouseNameAr: formData.warehouseNameAr,
          branchId: formData.branchId ?? undefined,
          keeperUserId: formData.keeperUserId ?? undefined,
          warehouseType: formData.warehouseType || undefined,
          inventoryAccountId: formData.inventoryAccountId ?? undefined,
        };
        await warehousesService.create(createDto);
      }
      onSave();
      handleClose();
    } catch (error: any) {
      console.error('Failed to save warehouse:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({
      warehouseCode: '',
      warehouseName: '',
      warehouseNameAr: '',
      branchId: null,
      keeperUserId: null,
      warehouseType: 'Main',
      inventoryAccountId: null,
      isActive: true,
    });
    setErrors({});
    onClose();
  };

  const selectedWarehouseType = warehouseTypes.find(wt => wt.value === formData.warehouseType) || warehouseTypes[0];
  const selectedAccount = accounts.find(acc => acc.accountId === formData.inventoryAccountId);
  const selectedBranch = branchOptions.find(b => b.branchId === formData.branchId) || null;
  const selectedKeeper = userOptions.find(u => u.userId === formData.keeperUserId) || null;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      dir={isRTL ? 'rtl' : 'ltr'}
      PaperProps={{ sx: { borderRadius: 3, bgcolor: isDarkMode ? '#1a1a2e' : 'white' } }}
    >
      <DialogTitle sx={{ background: isDarkMode ? 'linear-gradient(135deg, #0f3460 0%, #16213e 100%)' : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white', display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <WarehouseIcon />
          <Typography variant="h6" fontWeight="bold">{warehouse ? t.editWarehouse : t.addWarehouse}</Typography>
        </Box>
        <IconButton onClick={handleClose} sx={{ color: 'white' }}><CloseIcon /></IconButton>
      </DialogTitle>

      <DialogContent sx={{ mt: 2 }}>
        {/* Basic Information */}
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <BusinessIcon sx={{ color: '#667eea' }} />
            <Typography variant="h6" fontWeight="bold" sx={{ color: '#667eea' }}>{t.basicInfo}</Typography>
          </Box>
          <Divider sx={{ mb: 2 }} />

          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 2 }}>
            {/* Warehouse Code */}
            <Box sx={{ display: 'flex', gap: 1 }}>
              <TextField fullWidth label={t.warehouseCode} value={formData.warehouseCode} onChange={handleChange('warehouseCode')} disabled={!!warehouse} error={!!errors.warehouseCode} helperText={errors.warehouseCode} InputProps={{ startAdornment: <CodeIcon sx={{ mr: 1, color: 'action.active' }} /> }} />
              {!warehouse && <IconButton color="primary" onClick={handleGenerateCode} sx={{ bgcolor: 'rgba(102, 126, 234, 0.1)' }}><RefreshIcon /></IconButton>}
            </Box>

            {/* Warehouse Type */}
            <Autocomplete options={warehouseTypes} getOptionLabel={(option) => `${option.icon} ${isRTL ? option.labelAr : option.labelEn}`} value={selectedWarehouseType} onChange={(_, newValue) => { setFormData({ ...formData, warehouseType: newValue?.value || 'Main' }); }} renderInput={(params) => <TextField {...params} label={t.warehouseType} error={!!errors.warehouseType} helperText={errors.warehouseType} InputProps={{ ...params.InputProps, startAdornment: (<><CategoryIcon sx={{ ml: 1, mr: 1, color: 'action.active' }} />{params.InputProps.startAdornment}</>), }} />} />

            {/* Warehouse Name EN */}
            <TextField fullWidth label={`${t.warehouseName} *`} value={formData.warehouseName} onChange={handleChange('warehouseName')} error={!!errors.warehouseName} helperText={errors.warehouseName} required InputProps={{ startAdornment: <LanguageIcon sx={{ mr: 1, color: 'action.active' }} /> }} />

            {/* Warehouse Name AR */}
            <TextField fullWidth label={`${t.warehouseNameAr} *`} value={formData.warehouseNameAr} onChange={handleChange('warehouseNameAr')} error={!!errors.warehouseNameAr} helperText={errors.warehouseNameAr} required InputProps={{ startAdornment: <LanguageIcon sx={{ mr: 1, color: 'action.active' }} /> }} />

            {/* Branch Dropdown */}
            <Autocomplete
              options={branchOptions}
              getOptionLabel={(option) => `${option.branchCode} - ${isRTL ? option.branchNameAr : option.branchName}`}
              value={selectedBranch}
              onChange={(_, newValue) => {
                setFormData({ ...formData, branchId: newValue?.branchId || null });
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={`${t.branch} (${t.optional})`}
                  placeholder={t.selectBranch}
                  error={!!errors.branchId}
                  helperText={errors.branchId}
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <>
                        <BusinessIcon sx={{ ml: 1, mr: 1, color: 'action.active' }} />
                        {params.InputProps.startAdornment}
                      </>
                    ),
                  }}
                />
              )}
              renderOption={(props, option) => {
                const { key, ...otherProps } = props;
                return (
                  <li key={key} {...otherProps}>
                    <Box>
                      <Typography variant="body2" fontWeight="bold">{option.branchCode}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {isRTL ? option.branchNameAr : option.branchName}
                      </Typography>
                    </Box>
                  </li>
                );
              }}
            />

            {/* Keeper User Dropdown */}
            <Autocomplete
              options={userOptions}
              getOptionLabel={(option) => isRTL
                ? `${option.fullNameAr || option.fullName} (${option.userName})`
                : `${option.fullName} (${option.userName})`
              }
              value={selectedKeeper}
              onChange={(_, newValue) => {
                setFormData({ ...formData, keeperUserId: newValue?.userId || null });
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={`${t.keeper} (${t.optional})`}
                  placeholder={t.selectKeeper}
                  error={!!errors.keeperUserId}
                  helperText={errors.keeperUserId}
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <>
                        <PersonIcon sx={{ ml: 1, mr: 1, color: 'action.active' }} />
                        {params.InputProps.startAdornment}
                      </>
                    ),
                  }}
                />
              )}
              renderOption={(props, option) => {
                const { key, ...otherProps } = props;
                const roleName = option.userRoles?.[0]?.role
                  ? (isRTL ? option.userRoles[0].role.roleNameAr : option.userRoles[0].role.roleName)
                  : '';
                return (
                  <li key={key} {...otherProps}>
                    <Box>
                      <Typography variant="body2" fontWeight="bold">
                        {isRTL ? (option.fullNameAr || option.fullName) : option.fullName}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {option.userName} {roleName ? `• ${roleName}` : ''}
                      </Typography>
                    </Box>
                  </li>
                );
              }}
            />
          </Box>
        </Box>

        {/* Accounting Information */}
        <Box sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <AccountBalanceIcon sx={{ color: '#667eea' }} />
            <Typography variant="h6" fontWeight="bold" sx={{ color: '#667eea' }}>{t.accountingInfo}</Typography>
          </Box>
          <Divider sx={{ mb: 2 }} />

          <Autocomplete fullWidth options={accounts} getOptionLabel={(option) => `${option.accountCode} - ${isRTL ? option.accountNameAr : option.accountName} (${option.accountType})`} value={selectedAccount || null} onChange={(_, newValue) => { setFormData({ ...formData, inventoryAccountId: newValue?.accountId || null }); }} loading={loadingAccounts} renderInput={(params) => <TextField {...params} label={`${t.inventoryAccount} (${t.optional})`} placeholder={t.selectInventoryAccount} InputProps={{ ...params.InputProps, startAdornment: (<><AccountBalanceIcon sx={{ ml: 1, mr: 1, color: 'action.active' }} />{params.InputProps.startAdornment}</>), endAdornment: (<>{loadingAccounts ? <CircularProgress color="inherit" size={20} /> : null}{params.InputProps.endAdornment}</>), }} />} renderOption={(props, option) => { const { key, ...otherProps } = props; return (<li key={key} {...otherProps}><Box><Typography variant="body2" fontWeight="bold">{option.accountCode}</Typography><Typography variant="caption" color="text.secondary">{isRTL ? option.accountNameAr : option.accountName} • {option.accountType}</Typography></Box></li>); }} noOptionsText={t.noAccountsFound} />

          {!formData.inventoryAccountId && <Alert severity="info" sx={{ mt: 2 }}>{isRTL ? 'ربط المخزن بحساب المخزون النهائي يساعد في تتبع القيم المحاسبية بشكل دقيق' : 'Linking warehouse to a final inventory account helps track accounting values accurately'}</Alert>}
        </Box>

        {/* Active Status */}
        {warehouse && (
          <Box sx={{ mt: 3, p: 2, bgcolor: isDarkMode ? 'rgba(255,255,255,0.05)' : '#f5f5f5', borderRadius: 2 }}>
            <FormControlLabel control={<Switch checked={formData.isActive} onChange={handleChange('isActive')} color="success" />} label={<Typography variant="body1" fontWeight="bold">{t.isActive}</Typography>} />
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 3, bgcolor: isDarkMode ? 'rgba(255,255,255,0.05)' : '#f5f5f5' }}>
        <Button onClick={handleClose} disabled={loading} size="large">{t.cancel}</Button>
        <Button onClick={handleSubmit} variant="contained" disabled={loading} startIcon={loading ? <CircularProgress size={20} /> : null} size="large" sx={{ background: 'linear-gradient(45deg, #667eea 30%, #764ba2 90%)', boxShadow: '0 3px 5px 2px rgba(102, 126, 234, .3)', '&:hover': { background: 'linear-gradient(45deg, #5568d3 30%, #653d8a 90%)' } }}>{t.save}</Button>
      </DialogActions>
    </Dialog>
  );
};

export default WarehouseDialog;